'use client'

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material'
import type { ButtonProps } from '@mui/material'
import type { MouseEvent, ReactNode } from 'react'
import { useState } from 'react'

interface ConfirmBoxProps {
  open: boolean
  onProceed: () => void
  onCancel: () => void
  title?: string
  description: ReactNode
  proceedText?: string
  cancelText?: string
  typeToConfirm?: string
  proceedButtonColor?: ButtonProps['color']
  proceedDisabled?: boolean
  loading?: boolean
  zIndex?: number
}

export function ConfirmBox({
  open,
  onProceed,
  onCancel,
  description,
  typeToConfirm,
  title = 'Are you sure you want to continue?',
  proceedText = 'Yes',
  cancelText = 'No',
  proceedButtonColor = 'primary',
  proceedDisabled = false,
  loading = false,
  zIndex,
}: ConfirmBoxProps) {
  const [confirmInputText, setConfirmInputText] = useState('')

  return (
    <Dialog
      aria-describedby="alert-dialog-description"
      aria-labelledby="alert-dialog-title"
      onClose={(event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        if (!loading) onCancel()
      }}
      open={open}
      sx={zIndex !== undefined ? { zIndex } : undefined}
    >
      <DialogTitle id="alert-dialog-title" variant="h4">
        {title}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {description}
          </DialogContentText>
          {typeToConfirm ? (
            <TextField
              label="Type to confirm"
              onChange={(e) => {
                setConfirmInputText(e.target.value)
              }}
              value={confirmInputText}
              variant="outlined"
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={loading}
          onClick={(event: MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            onCancel()
          }}
        >
          {cancelText}
        </Button>
        <Button
          color={proceedButtonColor}
          disabled={
            proceedDisabled ||
            (typeToConfirm ? confirmInputText !== typeToConfirm : false)
          }
          loading={loading}
          onClick={(event: MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            onProceed()
          }}
          variant="contained"
        >
          {proceedText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
